import styled from "styled-components";
import { SermonContentStyle } from "../../style/SermonContentStyles";
import { TagBadgeStyle } from "../../style/SermonStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookBible,
  faCalendarDays,
  faUser,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../util/hook";
import { SermonData } from "../../contexts/SermonContext";
import ScriptureText from "../molecules/ScriptureText";

interface SermonViewProps {
  sermon: SermonData;
}

const SermonView = ({ sermon }: SermonViewProps) => {
  const { isLoggedIn } = useAuth();
  // HTML 내용이 실제로 비어있는지 체크 (빈 태그만 있는 경우 포함)
  const hasContent = (html: string | undefined): boolean => {
    if (!html) return false;
    // HTML 태그 제거 후 공백 제거
    const text = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return text.length > 0;
  };

  // 유튜브 URL을 임베드 URL로 변환
  const getYoutubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    // 일반 유튜브 URL 패턴: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    );
    if (watchMatch && watchMatch[1]) {
      return `https://www.youtube.com/embed/${watchMatch[1]}`;
    }

    return null;
  };

  const embedUrl = sermon.youtubeUrl
    ? getYoutubeEmbedUrl(sermon.youtubeUrl)
    : null;

  return (
    <>
      <SermonHeader>
        <CategoryBadge className={sermon.category}>
          {sermon.categoryName}
        </CategoryBadge>
        <SermonTitle>{sermon.title}</SermonTitle>
        <SermonMeta>
          <MetaItem>
            <FontAwesomeIcon icon={faBookBible} />
            {sermon.scripture.join(" / ")}
          </MetaItem>
          <MetaItem>
            <FontAwesomeIcon icon={faCalendarDays} />
            {sermon.date}
          </MetaItem>
          <MetaItem>
            <FontAwesomeIcon icon={faUser} />
            {sermon.preacher}
          </MetaItem>
          {isLoggedIn && (
            <MetaItem>
              <FontAwesomeIcon icon={faEye} />
              조회수 {(sermon as any).view_count || 0}
            </MetaItem>
          )}
        </SermonMeta>
      </SermonHeader>

      {sermon.scripture && sermon.scripture.length > 0 && (
        <ScriptureText scriptures={sermon.scripture} />
      )}

      {embedUrl && (
        <YoutubeSection>
          <YoutubeTitle>설교 영상</YoutubeTitle>
          <YoutubeWrapper>
            <iframe
              src={embedUrl}
              title="설교 영상"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </YoutubeWrapper>
        </YoutubeSection>
      )}

      {hasContent(sermon.content) && (
        <ContentSection>
          <ContentTitle>설교 내용</ContentTitle>
          <ContentBody dangerouslySetInnerHTML={{ __html: sermon.content! }} />
        </ContentSection>
      )}
    </>
  );
};

const SermonHeader = styled.div`
  text-align: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const CategoryBadge = styled.span`
  ${TagBadgeStyle}
  margin-bottom: 15px;
`;

const SermonTitle = styled.h1`
  font-size: 28px;
  color: #004094;
  margin-bottom: 20px;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SermonMeta = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;

  svg {
    color: #004094;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 40px;
`;

const ContentTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
`;

const ContentBody = styled.div`
  ${SermonContentStyle}
`;

const YoutubeSection = styled.div`
  margin: 40px 0;
  padding: 30px 0;
  border-bottom: 1px solid #eee;
  border-top: 1px solid #eee;
`;

const YoutubeTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
`;

const YoutubeWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export default SermonView;
